import {
  useQueries,
  type QueryFunctionContext,
  type UseQueryResult,
} from '@tanstack/react-query';
import { dataApiClient } from '../../infrastructure/dataApi/client';
import { useCurrentSimDataId } from '../SimViewer/useCurrentSimDataId';
import { useMetaData } from './useMetaData';
import { useApiDataFilter } from '../SimViewer/useApiDataFilter';
import { useSchemaComparison } from '../SimViewer/useSchemaComparison';

type UseSummaryComparisonsData = {
  isLoading: boolean;
  summariesMap: Record<string, number | undefined>;
};

const useSummaryComparisonsData = (): UseSummaryComparisonsData => {
  const { currentSimDataId } = useCurrentSimDataId();
  const { comparisons } = useSchemaComparison();
  const { allDataMetas } = useMetaData();
  const { apiFilter } = useApiDataFilter();

  const simDataIds = currentSimDataId ? [currentSimDataId, ...comparisons] : [];

  const results = useQueries({
    queries: simDataIds.map((simDataId: string) => {
      return {
        queryKey: ['summary', simDataId, apiFilter],
        queryFn: async ({ signal }: QueryFunctionContext): Promise<number> => {
          return dataApiClient.getSummary(simDataId, apiFilter, signal);
        },
        meta: { dataType: 'others' },
      };
    }),
  });

  return {
    isLoading: results.some((result: UseQueryResult): boolean => {
      return result.isFetching;
    }),
    summariesMap: Object.fromEntries(
      simDataIds.map(
        (simDataId: string, index: number): [string, number | undefined] => {
          const viewMagnification =
            1 / Number(allDataMetas.get(simDataId)?.reduction_factor ?? 1);
          return [
            simDataId,
            results[index].data !== undefined
              ? Math.floor(results[index].data * viewMagnification)
              : undefined,
          ];
        }
      )
    ),
  };
};

export { useSummaryComparisonsData };
